<template>
  <b-nav-item-dropdown right class="shopping-cart">
    <template slot="button-content" class="cart-button-feedback">
      <vue-element-loading :active="active_cart_is_busy" />
      <font-awesome-icon icon="shopping-cart" class="cart-icon mr-2"></font-awesome-icon>
      <span class="text-uppercase font-weight-semibold">{{ $t('shopping_cart.cart') }} ({{ lines }})</span>
    </template>
    <div class="products px-4">
      {{ $t('shopping_cart.products') }}:
      <ShoppingCartLine
        v-for="(product, index) in cartProducts"
        :key="index"
        :product="product"
        :priceUuid="index"
        class="my-3"
      />
    </div>
    <b-dropdown-divider></b-dropdown-divider>
    <div v-if="lines > 0" class="shipping-cost px-4">{{ $t('shopping_cart.courier') }}:</div>
    <b-dropdown-divider v-if="lines > 0"></b-dropdown-divider>
    <div v-if="lines > 0" class="total px-4 d-flex">
      {{ $t('shopping_cart.total') }}:
      <div class="ml-auto">
        <div class="text-right">{{ total }}</div>
      </div>
    </div>
    <b-button
      variant="outline-*"
      class="confirm-button"
      :disabled="lines == 0"
      @click="confirm"
    >{{ $t('shopping_cart.confirm') }}</b-button>
  </b-nav-item-dropdown>
</template>


<script>
import { mapGetters, mapState } from "vuex";
import ShoppingCartLine from "@/components/ShoppingCartLine";
import { buildAmount } from "@/lib/amount.js";

export default {
  name: "shopping-cart",
  components: {
    ShoppingCartLine,
  },
  computed: {
    ...mapGetters("shoppingcart", ["cartProducts", "cartNotification"]),
    ...mapGetters("auth", ["isAuthenticated"]),
    ...mapState({
      active_cart_is_busy: (state) => state.shoppingcart.active_cart_is_busy,
    }),
    lines() {
      return Object.keys(this.cartProducts).length;
    },
    total() {
      if (!this.cartProducts) {
        return undefined;
      }
      var amount = buildAmount(Object.values(this.cartProducts));
      return this.$formatMoney(amount.total.amount, amount.total.currency);
    },
  },
  watch: {
    cartNotification() {
      this.notifyProductAdded();
    },
    isAuthenticated: function () {
      if (this.isAuthenticated) {
        this.syncCartProducts();
      } else {
        this.clearCart();
      }
    },
  },
  mounted() {
    // Attach event listener to localStorage.
    // This event is only triggered when a window other than itself makes
    // the changes.
    // Doc: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#Responding_to_storage_changes_with_the_StorageEvent
    window.addEventListener(
      "storage",
      function (e) {
        // e = StorageEvent
        // Doc: https://developer.mozilla.org/en-US/docs/Web/API/StorageEvent

        // Ignore other than shoppingcart changes
        if (e.key != "shoppingcart") {
          return;
        }

        // Tell store to update shoppingcart state from localStorage
        this.$store.commit(
          "shoppingcart/MUTATE_SHOPPING_CART_FROM_LOCALSTORAGE"
        );

        // debug
        // console.log(e);
      }.bind(this),
      false
    );
  },
  methods: {
    confirm() {
      this.$router.push({
        name: "cart_review",
      });
    },
    notifyProductAdded() {
      this.mnotify({
        text: this.$t("shopping_cart.product_added"),
      });
    },
    loadCartProducts() {
      this.$store
        .dispatch("shoppingcart/REQUEST_ACTIVE_CART")
        .then(undefined, (err) => {
          this.$sentry({ capture: err });
          this.mnotify_error({
            text: this.$t("shopping_cart.error.load_products"),
          });
        });
    },
    syncCartProducts() {
      try {
        let dispatched = this.$store.dispatch(
          "shoppingcart/ADD_PRODUCTS_FROM_LOCALSTORAGE"
        );
        dispatched.then((promises) => {
          Promise.allSettled(promises).then(
            () => {
              this.loadCartProducts();
            },
            () => {
              this.loadCartProducts();
              this.mnotify_error({
                text: this.$t("shopping_cart.error.some_product"),
              });
            }
          );
        });
      } catch (e) {
        this.$sentry({ capture: e });
        this.mnotify_error({
          text: this.$t("shopping_cart.error.load_products"),
        });
      }
    },
    clearCart() {
      this.$store.dispatch("shoppingcart/CLEAR_CART");
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        shopping_cart: {
          cart: "Cart",
          products: "Products",
          courier: "Courier Service",
          total: "Total",
          confirm: "Confirm purchase",
          product_added: "Product successfully added",
          error: {
            some_product:
              "Some of the products in your cart cannot be processed.",
            load_products: "Cannot load shopping cart items",
            sync_products: "Cannot sync shopping cart items",
          },
        },
      },
    },
  },
};
</script>


<style lang="scss" scoped>
.shopping-cart ::v-deep .nav-link {
  text-transform: uppercase;
  text-align: center;
  font-size: $font-size-small;
}

.cart-icon {
  color: $corp-color1;
  font-size: $font-size-medium;
}

.shopping-cart ::v-deep .dropdown-menu {
  border-top: 3px solid $corp-color1;
  text-transform: uppercase;
  width: 30rem;
}

.products,
.shipping-cost {
  color: $text-main-color;
  font-size: $font-size-medium * 1.1;
}

.total {
  color: $text-main-color;
  font-size: $font-size-default;
  font-weight: bold;
}

.confirm-button {
  background-color: $corp-color2;
  color: $text-second-color;
  font-size: $font-size-default;
  font-weight: bold;
  margin: 1rem 5% 1rem 5%;
  width: 90%;
}
</style>
